import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Button, Select, Spin } from 'antd';
import {
  BookOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  PlusOutlined,
  AlertOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { trainingService } from '@iso/services';

const { Option } = Select;

const TrainingDashboard = ({ propertyId, onRunSession, onTabChange, canRunSession, hasSafetyRole, refreshKey }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    active_assets: 0,
    sessions_this_month: 0,
    expiring_in_30_days: 0,
    overdue_records: 0,
  });
  const [complianceStats, setComplianceStats] = useState({
    compliant: 0,
    expiring_soon: 0,
    expired: 0,
    not_competent: 0,
  });
  const [recentActivity, setRecentActivity] = useState([]);

  useEffect(() => {
    if (!propertyId) return;
    fetchDashboardData();
  }, [propertyId, refreshKey]);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [res, compRes] = await Promise.all([
        trainingService.getDashboardStats(propertyId),
        trainingService.getComplianceStats(propertyId),
      ]);
      if (res) {
        setStats(prev => ({ ...prev, ...(res.stats || {}) }));
        setRecentActivity(res.recent_activity || []);
      }
      if (compRes) {
        setComplianceStats(prev => ({ ...prev, ...(compRes.compliance_stats || {}) }));
      }
    } catch (e) {
      console.error('Failed to fetch dashboard data:', e);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    {
      title: 'Active Assets',
      value: stats.active_assets,
      icon: <BookOutlined />,
      color: '#0B2A1B',
      bg: '#e8f5e9',
    },
    {
      title: 'Sessions This Month',
      value: stats.sessions_this_month,
      icon: <CalendarOutlined />,
      color: '#1890ff',
      bg: '#e6f7ff',
    },
    {
      title: 'Expiring in 30 Days',
      value: stats.expiring_in_30_days,
      icon: <ClockCircleOutlined />,
      color: '#faad14',
      bg: '#fffbe6',
    },
    {
      title: 'Overdue Records',
      value: stats.overdue_records,
      icon: <ExclamationCircleOutlined />,
      color: '#f5222d',
      bg: '#fff1f0',
    },
  ];

  if (loading) {
    return (
      <div className="tab-content">
        <div className="section-card" style={{ textAlign: 'center', paddingTop: 60, paddingBottom: 60 }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div className="tab-content">
      {/* Filter Row */}
      <div className="section-card">
        <div className="filter-row">
          <Select className="filter-select" defaultValue="" placeholder="All Person Types">
            <Option value="">All Person Types</Option>
            <Option value="staff">Staff</Option>
            <Option value="visitor">Visitor</Option>
            <Option value="contractor">Contractor</Option>
          </Select>
          <Select className="filter-select" defaultValue="" placeholder="All Training Types">
            <Option value="">All Training Types</Option>
            <Option value="course">Course</Option>
            <Option value="training">Training</Option>
          </Select>
        </div>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {statCards.map((card, idx) => (
          <Col xs={24} sm={12} lg={6} key={idx}>
            <Card className="stat-card">
              <div className="stat-card-icon" style={{ background: card.bg, color: card.color }}>
                {card.icon}
              </div>
              <Statistic title={card.title} value={card.value} valueStyle={{ color: card.color }} />
            </Card>
          </Col>
        ))}
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        {/* Compliance Snapshot */}
        <Col xs={24} lg={12}>
          <div className="section-card compliance-snapshot" style={{ marginBottom: 0, height: '100%' }}>
            <div className="snapshot-title">Compliance Snapshot</div>
            <Row gutter={[12, 12]}>
              <Col span={6}>
                <div className="snapshot-item snapshot-compliant">
                  <span className="count">{complianceStats.compliant}</span>
                  <span className="label">Competent</span>
                </div>
              </Col>
              <Col span={6}>
                <div className="snapshot-item snapshot-expiring">
                  <span className="count">{complianceStats.expiring_soon}</span>
                  <span className="label">Expiring Soon</span>
                </div>
              </Col>
              <Col span={6}>
                <div className="snapshot-item snapshot-expired">
                  <span className="count">{complianceStats.expired}</span>
                  <span className="label">Expired</span>
                </div>
              </Col>
              <Col span={6}>
                <div className="snapshot-item snapshot-missing">
                  <span className="count">{complianceStats.not_competent}</span>
                  <span className="label">Not Competent</span>
                </div>
              </Col>
            </Row>
          </div>
        </Col>

        {/* Recent Activity */}
        <Col xs={24} lg={12}>
          <div className="section-card" style={{ marginBottom: 0, height: '100%' }}>
            <h3 className="section-title">Recent Activity</h3>
            {recentActivity.length === 0 ? (
              <div style={{ color: '#8c8c8c', textAlign: 'center', padding: '20px 0' }}>
                No recent activity
              </div>
            ) : (
              recentActivity.map((item, idx) => (
                <div className="activity-item" key={idx}>
                  <div className="activity-desc">{item.description}</div>
                  <div className="activity-meta">
                    {item.date} {item.trainer ? `· ${item.trainer}` : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        </Col>
      </Row>

      {/* Quick Actions */}
      <div className="section-card quick-actions" style={{ marginBottom: 0 }}>
        <div className="quick-action-title">Quick Actions</div>
        <Row gutter={[12, 12]}>
          {canRunSession && (
            <Col xs={24} sm={12} md={6}>
              <Button className="quick-action-btn" icon={<PlayCircleOutlined />} onClick={onRunSession}>
                Run New Training Session
              </Button>
            </Col>
          )}
          <Col xs={24} sm={12} md={6}>
            <Button className="quick-action-btn" icon={<PlusOutlined />} onClick={() => onTabChange('assets')}>
              Add Training Asset
            </Button>
          </Col>
          {hasSafetyRole && (
            <Col xs={24} sm={12} md={6}>
              <Button className="quick-action-btn" icon={<AlertOutlined />} onClick={() => onTabChange('compliance')}>
                View Expiring Records
              </Button>
            </Col>
          )}
          {hasSafetyRole && (
            <Col xs={24} sm={12} md={6}>
              <Button className="quick-action-btn" icon={<SearchOutlined />} onClick={() => onTabChange('people')}>
                Search Person
              </Button>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );
};

export default TrainingDashboard;
